import { useCallback, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import {
  browseSharePoint,
  type SpoBrowseResult,
  validateKvSecret,
  validateSpoToken,
} from "@/api/admin-api"

export type VerifyStepStatus = "idle" | "running" | "ok" | "error"

export type VerifyState = {
  step1: VerifyStepStatus
  step2: VerifyStepStatus
  step3: VerifyStepStatus
  step1Error?: string
  step2Error?: string
  step3Error?: string
}

export const INITIAL_VERIFY_STATE: VerifyState = {
  step1: "idle",
  step2: "idle",
  step3: "idle",
}

/**
 * What to verify. `auth.client_secret` is the Key Vault secret *name* typed by the admin, or
 * "" when relying on a library's stored reference resolved server-side via
 * `copy_auth_from_library_id` (typed, non-empty values always win over the stored ones).
 */
export type SharePointVerifyTarget = {
  hostname: string
  siteName: string
  listName?: string
  /** Preferred over listName by the browse endpoint when set (migrated libraries store only the id). */
  listId?: string
  folderPath?: string
  auth: { client_id: string; tenant_id: string; client_secret: string }
  copy_auth_from_library_id?: string
  /** Probe whether the app registration can read item permissions ("replicate permissions"). */
  check_permissions_scope?: boolean
  /** "list_items" for generic lists (spo-list / spo-avtal); defaults to "files". */
  itemKind?: "files" | "list_items"
}

function canRun(target: SharePointVerifyTarget): boolean {
  return Boolean(
    target.hostname &&
    target.siteName &&
    target.auth.client_id.trim() &&
    target.auth.tenant_id.trim() &&
    (target.auth.client_secret.trim() || target.copy_auth_from_library_id?.trim())
  )
}

/**
 * The 3-step SharePoint connection check shared by the add and edit library dialogs:
 *  1. KV secret exists
 *  2. Credentials can obtain an Azure AD token for SharePoint
 *  3. Credentials can list files in the target library (result feeds the file browser)
 */
export function useSharePointConnectionVerify() {
  const { t } = useTranslation()

  const [verifying, setVerifying] = useState(false)
  const [verifyState, setVerifyState] = useState<VerifyState>(INITIAL_VERIFY_STATE)

  const [browseOpen, setBrowseOpen] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [browseResult, setBrowseResult] = useState<SpoBrowseResult | null>(null)
  const [browseError, setBrowseError] = useState<string | null>(null)
  const [browseNextPageToken, setBrowseNextPageToken] = useState<string | undefined>(undefined)
  /** What the last run listed, so the UI can say "items" for generic lists. */
  const [itemKind, setItemKind] = useState<"files" | "list_items">("files")

  // Generation counter: every reset() or new runVerify() bumps it, and every completion of an
  // older run checks it before writing state. Without this, a verify that was in flight when
  // the admin changed the target/auth would still land its (now stale) results.
  const runRef = useRef(0)

  const reset = useCallback(() => {
    runRef.current += 1
    setVerifying(false)
    setItemKind("files")
    setVerifyState(INITIAL_VERIFY_STATE)
    setBrowseOpen(false)
    setIsLoadingMore(false)
    setBrowseResult(null)
    setBrowseError(null)
    setBrowseNextPageToken(undefined)
  }, [])

  const runVerify = useCallback(
    async (target: SharePointVerifyTarget) => {
      if (!canRun(target)) return

      const run = ++runRef.current
      const live = () => runRef.current === run
      const secretRef = target.auth.client_secret.trim()
      const copyId = target.copy_auth_from_library_id?.trim() || undefined
      const auth = { ...target.auth, client_secret: secretRef }

      setVerifying(true)
      setItemKind(target.itemKind ?? "files")
      setVerifyState(INITIAL_VERIFY_STATE)
      setBrowseResult(null)
      setBrowseError(null)
      setBrowseOpen(false)
      setIsLoadingMore(false)
      setBrowseNextPageToken(undefined)

      // Step 1 — KV secret exists
      setVerifyState((s) => ({ ...s, step1: "running" }))
      try {
        const r1 = secretRef
          ? await validateKvSecret(secretRef)
          : await validateKvSecret({ copy_auth_from_library_id: copyId ?? "" })
        if (!live()) return
        if (!r1.valid) {
          setVerifyState((s) => ({
            ...s,
            step1: "error",
            step1Error:
              r1.error ??
              t(
                "adminDashboard.libraries.connectionVerify.kvSecretNotFound",
                "Secret not found in Key Vault"
              ),
          }))
          setVerifying(false)
          return
        }
        setVerifyState((s) => ({ ...s, step1: "ok" }))
      } catch (e) {
        if (!live()) return
        setVerifyState((s) => ({
          ...s,
          step1: "error",
          step1Error: e instanceof Error ? e.message : "Key Vault check failed",
        }))
        setVerifying(false)
        return
      }

      // Step 2 — Get Azure AD token
      setVerifyState((s) => ({ ...s, step2: "running" }))
      try {
        const r2 = await validateSpoToken({
          hostname: target.hostname,
          auth,
          copy_auth_from_library_id: copyId,
        })
        if (!live()) return
        if (!r2.valid) {
          setVerifyState((s) => ({
            ...s,
            step2: "error",
            step2Error:
              r2.error ??
              t(
                "adminDashboard.libraries.connectionVerify.tokenFailed",
                "Failed to obtain access token"
              ),
          }))
          setVerifying(false)
          return
        }
        setVerifyState((s) => ({ ...s, step2: "ok" }))
      } catch (e) {
        if (!live()) return
        setVerifyState((s) => ({
          ...s,
          step2: "error",
          step2Error: e instanceof Error ? e.message : "Token acquisition failed",
        }))
        setVerifying(false)
        return
      }

      // Step 3 — List files in SharePoint
      setVerifyState((s) => ({ ...s, step3: "running" }))
      try {
        const r3 = await browseSharePoint({
          hostname: target.hostname,
          siteName: target.siteName,
          listName: target.listName,
          listId: target.listId,
          folderPath: target.folderPath,
          auth,
          copy_auth_from_library_id: copyId,
          check_permissions_scope: target.check_permissions_scope === true,
          item_kind: target.itemKind,
        })
        if (!live()) return
        setBrowseResult(r3)
        setBrowseNextPageToken(r3.next_page_token)
        setBrowseOpen(true)
        setVerifyState((s) => ({ ...s, step3: "ok" }))
      } catch (e) {
        if (!live()) return
        setVerifyState((s) => ({
          ...s,
          step3: "error",
          step3Error: e instanceof Error ? e.message : "Failed to list SharePoint files",
        }))
      } finally {
        if (live()) setVerifying(false)
      }
    },
    [t]
  )

  const loadMore = useCallback(
    async (target: SharePointVerifyTarget) => {
      if (!browseNextPageToken || !canRun(target)) return

      // Continues the current run: a reset or a newer verify in the meantime discards this page.
      const run = runRef.current
      const live = () => runRef.current === run
      const secretRef = target.auth.client_secret.trim()
      const copyId = target.copy_auth_from_library_id?.trim() || undefined

      setIsLoadingMore(true)
      try {
        const result = await browseSharePoint({
          hostname: target.hostname,
          siteName: target.siteName,
          listName: target.listName,
          listId: target.listId,
          folderPath: target.folderPath,
          auth: { ...target.auth, client_secret: secretRef },
          copy_auth_from_library_id: copyId,
          next_page_token: browseNextPageToken,
          item_kind: target.itemKind,
        })
        if (!live()) return
        setBrowseResult((prev) =>
          prev ? { ...result, files: [...prev.files, ...result.files] } : result
        )
        setBrowseNextPageToken(result.next_page_token)
      } catch (e) {
        if (!live()) return
        setBrowseError(e instanceof Error ? e.message : "Failed to load more files")
      } finally {
        if (live()) setIsLoadingMore(false)
      }
    },
    [browseNextPageToken]
  )

  const verifyDone =
    verifyState.step1 !== "idle" || verifyState.step2 !== "idle" || verifyState.step3 !== "idle"

  const allStepsOk =
    verifyState.step1 === "ok" && verifyState.step2 === "ok" && verifyState.step3 === "ok"

  return {
    verifying,
    verifyState,
    itemKind,
    verifyDone,
    allStepsOk,
    browseOpen,
    setBrowseOpen,
    browseResult,
    browseError,
    browseNextPageToken,
    isLoadingMore,
    reset,
    runVerify,
    loadMore,
  }
}

export type SharePointConnectionVerify = ReturnType<typeof useSharePointConnectionVerify>
